// src/components/MemberTable/PaginationControls.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './PaginationControls.css';
import { useTranslation } from 'react-i18next';

const PaginationControls = ({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  onPageClick,
  itemsPerPage,
  onItemsPerPageChange,
}) => {
  const { t } = useTranslation();

  // Implement your own method to detect mobile devices
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to generate page numbers based on the current page and total pages
  const generatePageNumbers = () => {
    const pageNumbers = [];

    if (isMobile) {
      // Show only 3 pages at a time in mobile view
      let startPage = Math.max(currentPage - 1, 1);
      let endPage = Math.min(startPage + 2, totalPages);

      // Adjust startPage if we are at the end
      if (endPage - startPage < 2) {
        startPage = Math.max(endPage - 2, 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show all page numbers in desktop view
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div className="pagination-controls-container">
      <div className="pagination-options">
        <label htmlFor="itemsPerPage">{t('pagination.membersPerPage')}</label>
        <select id="itemsPerPage" value={itemsPerPage} onChange={onItemsPerPageChange}>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
      <div className="pagination-controls">
        <Button
          variant="secondary"
          onClick={onPrev}
          disabled={currentPage === 1}
          aria-label={t('pagination.previous')}
        >
          {isMobile ? <FontAwesomeIcon icon={faChevronLeft} /> : t('pagination.previous')}
        </Button>
        {pageNumbers.map((number) => (
          <Button
            key={number}
            variant="secondary"
            className={number === currentPage ? 'selected-page-button' : 'page-button'}
            onClick={() => onPageClick(number)}
            aria-label={t('pagination.page', { number })}
          >
            {number}
          </Button>
        ))}
        <Button
          variant="secondary"
          onClick={onNext}
          disabled={currentPage === totalPages}
          aria-label={t('pagination.next')}
        >
          {isMobile ? <FontAwesomeIcon icon={faChevronRight} /> : t('pagination.next')}
        </Button>
      </div>
    </div>
  );
};

PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPageClick: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onItemsPerPageChange: PropTypes.func.isRequired,
};

export default PaginationControls;
